// /oversight/notes?project=<id>[&review=pending][&shift=<shiftId>] (architecture sprint-12 §9.2; sprint-15 §9.1).
// Member notes for one project the caller reads notes on (sprint 15: every Supervisor of a notes-on project); the
// first by name is the default. A review notification opens it filtered to one shift. Route guard:
// me.access.notes || Admin (OversightRoute).
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { NotesPanel } from '@/components/notes/NotesPanel';
import { EmptyState } from '@/components/EmptyState';
import { Page } from '@/components/Page';
import { PageHeader } from '@/components/PageHeader';
import { ProjectScopeSelect, resolveScope } from '@/components/ProjectScopeSelect';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useMe } from '@/hooks/use-me';
import { useNotesReviewCounts } from '@/hooks/use-notes-review';
import { useOversightProjects } from '@/hooks/use-oversight';
import { notificationPermission, requestNotificationPermission } from '@/lib/notifications';
import type { OversightProject } from '@/types/settings';

export const TURN_ON_REVIEW_NOTIFICATIONS = 'Turn on review notifications';

export function hasNoteCapability(p: OversightProject): boolean {
  return p.caps.addNotes || p.caps.readNotes;
}

/** Sprint 15 (§8): Supervisors who get review notifications are asked once for the browser permission. */
function ReviewPermissionButton(): JSX.Element | null {
  const [state, setState] = useState(() => notificationPermission());
  if (state !== 'default') return null;
  return (
    <Button variant="outline" onClick={() => void requestNotificationPermission().then(setState)}>
      {TURN_ON_REVIEW_NOTIFICATIONS}
    </Button>
  );
}

export function OversightNotesPage(): JSX.Element {
  const { data: me } = useMe();
  const projects = useOversightProjects();
  if (!me || !projects.data)
    return (
      <Page width="4xl">
        <Skeleton className="h-96 w-full" />
      </Page>
    );
  const choices = projects.data.filter(hasNoteCapability);
  if (choices.length === 0)
    return (
      <Page width="4xl">
        <PageHeader title="Notes" />
        <EmptyState title="No projects with member notes." />
      </Page>
    );
  return <NotesView tz={me.timezone} choices={choices} notify={me.access.notesReviewNotify === true} />;
}

function NotesView({ tz, choices, notify }: { tz: string; choices: OversightProject[]; notify: boolean }): JSX.Element {
  const [params, setParams] = useSearchParams();
  const { byProject } = useNotesReviewCounts();
  const { scope, valid } = resolveScope(params.get('project'), choices, false);
  const project = choices.find((p) => p.id === scope) ?? null;
  const review = params.get('review') === 'pending';
  const shiftId = params.get('shift');
  // The live feed count when there is one (Supervisors), else the list's count (Admins' feed covers notes-on only).
  const countOf = (p: OversightProject): number => byProject.get(p.id) ?? p.pendingReviewCount ?? 0;
  const labelled = choices.map((p) => ({ ...p, name: countOf(p) > 0 ? `${p.name} (${countOf(p)})` : p.name }));

  useEffect(() => {
    if (!valid && project) setParams({ project: project.id }, { replace: true });
  }, [valid, project, setParams]);

  function update(next: { review?: boolean; shift?: string | null }): void {
    if (!project) return;
    const q: Record<string, string> = { project: project.id };
    const r = next.review ?? review;
    const s = next.shift !== undefined ? next.shift : shiftId;
    if (r) q['review'] = 'pending';
    if (s) q['shift'] = s;
    setParams(q);
  }

  return (
    <Page width="4xl">
      <PageHeader title="Notes" hint="Notes about the members of a project you supervise." actions={notify ? <ReviewPermissionButton /> : undefined} />
      <div className="mb-4">
        <ProjectScopeSelect id="oversight-notes-project" value={project?.id ?? ''} projects={labelled} allowAll={false} onChange={(id) => setParams({ project: id })} />
      </div>
      {/* Keyed so the filters reset when the project changes. */}
      {project ? (
        <NotesPanel
          key={project.id}
          project={{ ...project, pendingReviewCount: countOf(project) }}
          timezone={tz}
          review={review}
          shiftId={shiftId}
          onReviewChange={(r) => update({ review: r, shift: null })}
          onClearShift={() => update({ shift: null })}
        />
      ) : (
        <Skeleton className="h-72 w-full rounded-xl" />
      )}
    </Page>
  );
}
